/** Presentation aliases only: these names never become stored configuration keys. */
const FIELDS = new Map(Object.entries({
  replaceable_fields: '可替换字段', reconciliation_policy: '内容更新规则', field_rules: '字段处理规则',
  allow_auto_replace: '允许自动替换', requires_user_confirmation: '需要用户确认',
  field_key: '字段', match_terms: '匹配词', value_terms: '值匹配词', value_markers: '值标记词',
  change_type: '变更类型', auto_apply: '自动应用', decision: '处理决定',
  artifact_types: '资产类型', artifact_actions: '资产更新方式', dependent_content_actions: '关联内容更新方式',
  inherit_unit_on_bare_value: '沿用原有单位', allow_text_value: '允许文本值',
  field_definitions: '字段定义', field_semantics: '字段语义', business_meaning: '说明',
  priority: '规则填写要求', blocker: '阻断检查', question: '收集问题', value_type: '数据格式',
  criticality: '重要程度', data_standard: '填写标准', example: '示例', required_by_modules: '必填模块',
  extraction_patterns: '识别规则', value_exclusion_patterns: '排除规则',
  required_fields: '必填字段', recommended_fields: '推荐字段', optional_fields: '可选字段',
  conditional_required_fields: '条件必填字段', field_weights: '字段权重', fields: '字段列表',
  field_defaults: '字段默认值', field_default_candidates: '字段默认候选值', readiness: '就绪条件',
  required: '必填', accepted_states: '接受状态', enabled: '启用', modules: '模块',
  module_field_sets: '模块字段集合', condition: '条件', value: '值', values: '值列表',
  title: '名称', label: '显示名称', name: '名称', description: '说明', key: '标识', id: '标识',
  type: '类型', source: '来源', target: '目标', weight: '权重', confidence: '置信度',
  confidence_defaults: '默认置信度', confirmed: '已确认', candidate: '候选', missing: '缺失',
  normalized_value: '标准值', needs_confirmation: '需要确认', evidence_refs: '依据引用',
  package_schema: '需求结构', groups: '分组', field_keys: '字段', relations: '关联关系',
  display: '展示设置', decomposition: '拆分设置', contract_version: '契约版本',
  workplan_blueprints: '执行流程', steps: '流程步骤', blueprint_id: '流程标识', step_id: '步骤标识',
  owner_capability: '所属能力', input: '输入', output: '输出', execution_requirements: '执行要求',
  dependency_step_ids: '前置步骤', capability_registry: '能力目录', capabilities: '能力',
  capability_id: '能力标识', skill_definitions: '技能', skill_id: '技能标识', icon_key: '图标',
  execution_templates: '执行策略', completion_policy_key: '完成策略', freshness_policy_key: '时效策略',
  reuse_policy_key: '复用策略', search_mapping: '搜索映射', queries: '查询', keywords: '关键词',
  categories: '分类', aliases: '别名', taxonomy: '分类体系', default: '默认值',
  requirement: '需求', analysis: '分析', sourcing: '寻源', rfx: '询报价',
  requirements_document: '需求文档', analysis_report: '分析报告', sourcing_result: '寻源结果', rfx_strategy: '询报价策略',
}))

export const fieldLabel = (key: string | number, index: number): string => {
  if (typeof key === 'number') return `第 ${key + 1} 项`
  if (FIELDS.has(key)) return FIELDS.get(key)!
  if (/\p{Script=Han}/u.test(key)) return key
  return `未配置名称 · ${index + 1}`
}

export const VALUE_TYPES = [
  { value: 'string', label: '文本' }, { value: 'number', label: '数字' },
  { value: 'boolean', label: '开关' }, { value: 'null', label: '空值' },
  { value: 'object', label: '分组' }, { value: 'array', label: '列表' },
]

const ACTIONS = [
  { value: 'patch', label: '局部更新' }, { value: 'mark_stale', label: '标记过期' },
  { value: 'regenerate_recommended', label: '建议重新生成' },
]
/** Display names for declared configuration choices, not a replacement for backend validation. */
const CHOICES = new Map(Object.entries({
  priority: [{ value: 'required', label: '必填' }, { value: 'recommended', label: '推荐' }, { value: 'optional', label: '可选' },
    { value: 'conditional_required', label: '条件必填' }],
  value_type: [{ value: 'text', label: '文本' }, { value: 'enum_or_text', label: '选项或文本' },
    { value: 'quantity', label: '数量' }, { value: 'money', label: '金额' }, { value: 'time_window', label: '时间范围' },
    { value: 'location', label: '地点' }, { value: 'score', label: '评分' }, { value: 'file', label: '文件' },
    { value: 'identifier', label: '编号' }],
  criticality: [{ value: 'critical', label: '关键' }, { value: 'important', label: '重要' }, { value: 'conditional', label: '条件适用' }],
  change_type: [{ value: 'field_value_update', label: '字段值更新' }, { value: 'purpose_change', label: '目的变更' },
    { value: 'category_change', label: '品类变更' }],
  decision: [{ value: 'needs_confirmation', label: '需要确认' }],
  artifact_actions: ACTIONS, dependent_content_actions: ACTIONS,
  artifact_types: [{ value: 'requirements_document', label: '需求文档' }, { value: 'analysis_report', label: '分析报告' },
    { value: 'sourcing_result', label: '寻源结果' }, { value: 'rfx_strategy', label: '询报价策略' }],
  accepted_states: [{ value: 'accepted', label: '已接受' }, { value: 'confirmed', label: '已确认' },
    { value: 'candidate', label: '候选' }, { value: 'missing', label: '缺失' }],
}))

export const valueChoices = (path: readonly (string | number)[], value: string) => {
  const choices = CHOICES.get(String(path.slice(-1)[0])) ?? CHOICES.get(String(path.slice(-2)[0]))
  if (!choices) return undefined
  return choices.some((item) => item.value === value) ? choices : [...choices, { value, label: '未配置名称' }]
}

/** MDXEditor supplies a translation hook, including its portalled menus and dialogs. */
const EDITOR = new Map(Object.entries({
  'dialogControls.save': '保存', 'dialogControls.cancel': '取消', 'dialog.close': '关闭',
  'toolbar.undo': '撤销', 'toolbar.redo': '重做', 'toolbar.bold': '加粗', 'toolbar.removeBold': '取消加粗',
  'toolbar.italic': '斜体', 'toolbar.removeItalic': '取消斜体', 'toolbar.underline': '下划线',
  'toolbar.removeUnderline': '取消下划线', 'toolbar.bulletedList': '无序列表', 'toolbar.numberedList': '有序列表',
  'toolbar.checkList': '任务列表', 'toolbar.link': '插入链接', 'toolbar.table': '插入表格',
  'toolbar.blockTypes.paragraph': '正文', 'toolbar.blockTypes.quote': '引用',
  'toolbar.blockTypes.heading': '{{level}} 级标题', 'toolbar.blockTypeSelect.selectBlockTypeTooltip': '选择段落格式',
  'toolbar.blockTypeSelect.placeholder': '段落格式', 'toolbar.toggleGroup': '格式选项',
  'toolbar.inlineCode': '行内代码', 'toolbar.removeInlineCode': '取消代码格式',
  'toolbar.codeBlock': '插入代码块', 'toolbar.thematicBreak': '插入分隔线',
  'toolbar.strikethrough': '删除线', 'toolbar.removeStrikethrough': '取消删除线',
  'toolbar.superscript': '上标', 'toolbar.removeSuperscript': '取消上标',
  'toolbar.subscript': '下标', 'toolbar.removeSubscript': '取消下标',
  'toolbar.highlight': '高亮', 'toolbar.removeHighlight': '取消高亮',
  'toolbar.richText': '可视编辑', 'toolbar.source': '源码', 'toolbar.diffMode': '对比',
  'toolbar.image': '插入图片', 'toolbar.editFrontmatter': '编辑文档属性', 'toolbar.insertFrontmatter': '插入文档属性',
  'createLink.url': '链接地址', 'createLink.urlPlaceholder': '输入或粘贴链接地址',
  'createLink.text': '显示文字', 'createLink.textTooltip': '链接显示的文字',
  'createLink.title': '链接标题', 'createLink.titleTooltip': '鼠标悬停时显示的标题',
  'createLink.saveTooltip': '保存链接', 'createLink.cancelTooltip': '取消修改',
  'linkPreview.edit': '编辑链接', 'linkPreview.copyToClipboard': '复制链接',
  'linkPreview.copied': '已复制', 'linkPreview.remove': '移除链接', 'linkPreview.open': '打开链接',
  'table.deleteTable': '删除表格', 'table.columnMenu': '列操作', 'table.rowMenu': '行操作',
  'table.textAlignment': '文字对齐', 'table.alignLeft': '左对齐', 'table.alignCenter': '居中',
  'table.alignRight': '右对齐', 'table.insertColumnLeft': '向左插入列', 'table.insertColumnRight': '向右插入列',
  'table.deleteColumn': '删除此列', 'table.insertRowAbove': '向上插入行',
  'table.insertRowBelow': '向下插入行', 'table.deleteRow': '删除此行',
  'codeBlock.selectLanguage': '选择代码语言', 'codeBlock.language': '代码语言',
  'codeBlock.inlineLanguage': '代码语言', 'codeblock.delete': '删除代码块',
}))

export const translateEditor = (key: string, _defaultValue: string, values?: Record<string, unknown>) => {
  const text = EDITOR.get(key) ?? '未配置名称'
  return text.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => String(values?.[name] ?? ''))
}
